import * as React from 'react';

import moment from 'moment';
import Dinero from 'dinero.js';
import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import { Serie } from '@nivo/line';

import SidebarEarningsBox from './sidebar-earnings-box';
import AppState from '@webapp/store/state';
import { connect } from 'react-redux';
import DownloadBox from './download-box';
import EarningListItem from './metrics-widget/earnings-list-item';
import { AdminGetStatementResponse } from '../../../../../services/royalties/types/api/admin-get-statements-response';
import { aggregateStatements } from '../../store/royalties/selectors';
import AnalyticsWidgetGraph from '@components/dashboard/analytics-widget/graph';
import EarningDeductionBox from './earning-deduction-box';
import EarningsFilter, { FILTER_OPTIONS } from './earnings-filter';
import { Hint } from '@components/basics/hint';
import { useLocation } from 'react-router-dom';

interface EarningsPageProps {
  className?: string;
  isMobile?: boolean;
  artistId: string;
  isExampleData: boolean;
  statements: AdminGetStatementResponse;
  currentDistributionPlan: string;
  currentMerchPlan: string;
}

const EARNINGS_DEDUCTIONS_START_DATE = '2023-02';

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
  artistId: state.artist.active?.id,
  statements:
    state.royalties.artists[state.artist.active?.id || '']?.statements || [],
  isExampleData:
    (state.royalties.artists[state.artist.active?.id || '']?.statements || [])
      .length === 0,
  currentDistributionPlan:
    state.subscriptionManager.summary?.artists
      .find((artist) => artist.id === state.artist.active?.id)
      ?.subscriptions.find((plan) =>
        ['distribution', 'trac-pro'].includes(plan.type)
      )?.plan ?? 'beginner',
  currentMerchPlan:
    state.subscriptionManager.summary?.artists
      .find((artist) => artist.id === state.artist.active?.id)
      ?.subscriptions.find((plan) => plan.type === 'merch')?.plan ?? 'beginner',
});

export const formatNumber = (n: number) => {
  const value = Intl.NumberFormat().format(n);
  return value;
};

export const EarningsPage: React.FC<EarningsPageProps> = ({
  className,
  artistId,
  isExampleData,
  statements,
  currentDistributionPlan,
  currentMerchPlan,
}) => {
  const { state } = useLocation<{ range?: string }>();
  const [reportDate, setReportDate] = React.useState(moment());
  const [dateRange, setDateRange] = React.useState('latestMonth');
  const [filteredStatements, setFilteredStatements] =
    React.useState(statements);
  const [deduction, setDeduction] = React.useState('');
  const [lineGraphData, setLineGraphData] = React.useState<Serie[]>([]);
  const [total, setTotal] = React.useState<Dinero.Dinero>(
    Dinero({ amount: 0, currency: 'USD' })
  );
  const [thisMonthEarned, setThisMonthEarned] = React.useState<Dinero.Dinero>(
    Dinero({ amount: 0, currency: 'USD' })
  );
  const [streamingEarnings, setStreamingEarnings] =
    React.useState<Dinero.Dinero>(Dinero({ amount: 0, currency: 'USD' }));
  const [merchEarnings, setMerchEarnings] = React.useState<Dinero.Dinero>(
    Dinero({ amount: 0, currency: 'USD' })
  );
  const [publishingEarnings, setPublishingEarnings] =
    React.useState<Dinero.Dinero>(Dinero({ amount: 0, currency: 'USD' }));
  const [streamingPercentage, setStreamingPercentage] = React.useState(0);
  const [merchPercentage, setMerchPercentage] = React.useState(0);
  const [publishingPercentage, setPublishingPercentage] = React.useState(0);

  React.useEffect(() => {
    if (state && state.range) {
      setDateRange(state.range);
    }
  }, [location]);

  React.useEffect(() => {
    if (dateRange === 'latestMonth') {
      setFilteredStatements(
        statements.length > 0 ? [statements[statements.length - 1]] : []
      );
    }
    if (dateRange === 'allTime') {
      setFilteredStatements(statements);
    }
    if (dateRange === 'lastWeek') {
      setFilteredStatements(
        statements.filter((s) =>
          moment(`${s.eligibleDate}-01`).isBetween(
            moment().subtract(7, 'days'),
            moment()
          )
        )
      );
    }
    if (dateRange === 'lastMonth') {
      setFilteredStatements(
        statements.filter((s) =>
          moment(`${s.eligibleDate}-01`).isBetween(
            moment().subtract(30, 'days'),
            moment()
          )
        )
      );
    }
    if (dateRange === 'last3Months') {
      setFilteredStatements(
        statements.filter((s) =>
          moment(`${s.eligibleDate}-01`).isBetween(
            moment().subtract(90, 'days'),
            moment()
          )
        )
      );
    }
    if (dateRange === 'lastYear') {
      setFilteredStatements(
        statements.filter((s) =>
          moment(`${s.eligibleDate}-01`).isBetween(
            moment().subtract(365, 'days'),
            moment()
          )
        )
      );
    }
  }, [dateRange, statements]);

  React.useEffect(() => {
    if (statements.length > 0) {
      setReportDate(
        moment(`${statements[statements.length - 1].eligibleDate}-01`)
      );
    }
    setTotal(aggregateStatements(statements, 'earnings'));
    if (isExampleData) {
      return;
    }
    setLineGraphData([
      {
        id: 'streaming',
        color: '#086EE5',
        data: statements.map((s) => ({
          x: new Date(`${s.eligibleDate}-01`),
          y: Dinero(s.streaming).toRoundedUnit(2) || 0,
        })),
      },
      {
        id: 'merch',
        color: '#A1A5B4',
        data: statements.map((s) => ({
          x: new Date(`${s.eligibleDate}-01`),
          y: Dinero(s.merch).toRoundedUnit(2) || 0,
        })),
      },
    ]);
  }, [isExampleData, statements]);

  React.useEffect(() => {
    setThisMonthEarned(aggregateStatements(filteredStatements, 'earnings'));
    setDeduction(
      aggregateStatements(filteredStatements, 'streaming')
        .percentage(10)
        .toFormat()
    );
    setStreamingEarnings(aggregateStatements(filteredStatements, 'streaming'));
    setMerchEarnings(aggregateStatements(filteredStatements, 'merch'));
    setPublishingEarnings(aggregateStatements([], 'earnings'));
  }, [filteredStatements]);

  React.useEffect(() => {
    if (thisMonthEarned.toUnit() > 0) {
      setStreamingPercentage(
        (streamingEarnings.toUnit() / thisMonthEarned.toUnit()) * 100
      );
      setMerchPercentage(
        (merchEarnings.toUnit() / thisMonthEarned.toUnit()) * 100
      );
      setPublishingPercentage(
        (publishingEarnings.toUnit() / thisMonthEarned.toUnit()) * 100
      );
    }
  }, [thisMonthEarned, streamingEarnings, merchEarnings, publishingEarnings]);

  const ITEMS = [
    {
      id: 'MUSIC',
      title: 'EARNINGS.LIST.MUSIC.TITLE',
      titleHelper: 'EARNINGS.LIST.MUSIC.TITLE.HELPER',
      totalEarnings: isExampleData
        ? Dinero({ amount: 714588, precision: 2, currency: 'USD' })
        : streamingEarnings,
      amount: 0,
      subtitle: 'EARNINGS.LIST.MUSIC.SUBTITLE',
      isComingSoon: false,
      items: [],
      color: 'rgba(8, 110, 229, 1)',
    },
    {
      id: 'MERCH',
      title: 'EARNINGS.LIST.MERCH.TITLE',
      titleHelper: 'EARNINGS.LIST.MERCH.TITLE.HELPER',
      totalEarnings: isExampleData
        ? Dinero({ amount: 714588, precision: 2, currency: 'USD' })
        : merchEarnings,
      amount: 0,
      subtitle: 'EARNINGS.LIST.MERCH.SUBTITLE',
      isComingSoon: false,
      items: [],
      color: 'rgba(8, 110, 229, 0.5)',
    },
    {
      id: 'PUBLISHING',
      title: 'EARNINGS.LIST.PUBLISHING.TITLE',
      titleHelper: 'EARNINGS.LIST.PUBLISHING.TITLE.HELPER',
      totalEarnings: isExampleData
        ? Dinero({ amount: 714588, precision: 2, currency: 'USD' })
        : publishingEarnings,
      amount: 0,
      subtitle: '',
      isComingSoon: true,
      items: [],
      color: 'rgba(8, 110, 229, 0.15)',
    },
  ];

  return (
    <>
      {isExampleData && (
        <StyledHint
          className="exampleHint"
          customTitle={<Translate id="EARNINGS.EXAMPLE_DATA.TITLE" />}
          noHelperText={true}
        >
          <Translate id="EARNINGS.EXAMPLE_DATA.DESCRIPTION" />
        </StyledHint>
      )}
      <EarningsContainer className={className}>
        <div className="info">
          <InfoHeader justifyContent="space-between" alignItems="center">
            <DateWrapper>
              <Typography type="section-title" customFont="semi-bold">
                <Translate id="EARNINGS.SIDEBAR.ITEM.EARNINGS.TITLE" />
              </Typography>
              <Typography style={{ color: 'grey' }} customFont="semi-bold">
                {dateRange === 'latestMonth'
                  ? reportDate.format('MMMM YYYY')
                  : FILTER_OPTIONS.find((o) => o.value == dateRange)?.label}
              </Typography>
            </DateWrapper>
          </InfoHeader>
          <LineGraphContainer>
            <AnalyticsWidgetGraph
              data={lineGraphData}
              isExampleData={isExampleData}
            />
          </LineGraphContainer>
          <List>
            {ITEMS.map((item, i) => (
              <EarningListItem
                key={`${item.id}-${i}`}
                id={item.id}
                reportDate={reportDate.format('YYYY-MM')}
                title={<Translate id={item.title} />}
                titleHelper={<Translate id={item.titleHelper} />}
                totalEarnings={item.totalEarnings}
                items={item.items}
                isComingSoon={item.isComingSoon}
                plan={
                  item.id === 'merch' && currentMerchPlan
                    ? currentMerchPlan
                    : currentDistributionPlan
                    ? currentDistributionPlan
                    : ''
                }
                hasDeduction={reportDate.isAfter(
                  moment(EARNINGS_DEDUCTIONS_START_DATE)
                )}
                dateRange={dateRange}
              />
            ))}
          </List>
        </div>
        <div className="sidebar">
          <div style={{ marginLeft: 'auto', marginRight: '0px' }}>
            <EarningsFilter
              setDateRange={setDateRange}
              currentValue={dateRange}
            />
          </div>
          {currentDistributionPlan.toLowerCase() === 'beginner' && (
            <EarningDeductionBox
              text="EARNINGS.DEDUCTIONS.TEXT"
              total={deduction}
            />
          )}
          <SidebarEarningsBox
            earned={
              isExampleData
                ? Dinero({ amount: 12456, precision: 0, currency: 'USD' })
                : total
            }
            thisMonthEarned={
              isExampleData
                ? Dinero({ amount: 2124, precision: 0, currency: 'USD' })
                : thisMonthEarned
            }
            streamingPercenatge={isExampleData ? 65 : streamingPercentage}
            merchPercentage={isExampleData ? 23 : merchPercentage}
            publishingPercentage={isExampleData ? 12 : publishingPercentage}
          />
          <DownloadBox artistId={artistId} />
        </div>
      </EarningsContainer>
    </>
  );
};

export default connect(mapStateToProps)(styled(EarningsPage)`
  .info,
  .sidebar {
    height: fit-content;
    display: grid;
    grid-gap: 30px;
    @media ${({ theme }) => theme.device.tablet} {
      grid-gap: 20px;
    }
  }

  .info {
    margin-top: 24px;
    display: grid;
    grid-gap: 30px;
    grid-row: 1;
    @media ${({ theme }) => theme.device.tablet} {
      grid-gap: 20px;
      grid-column: 2 / 14;
      grid-row: 1;
      margin-top: 0;
    }
    @media ${({ theme }) => theme.device.laptop} {
      grid-column: 4 / 14;
    }
  }
  .sidebar {
    .filter {
      align: right;
    }
    grid-row: 2;
    @media ${({ theme }) => theme.device.tablet} {
      grid-column: 15 / 24;
      grid-row: 1;
    }
    @media ${({ theme }) => theme.device.laptop} {
      grid-column: 15 / 22;
    }
  }
`);

export const EarningsContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  min-height: 100vh;
  padding: 0 24px 24px;

  @media ${({ theme }) => theme.device.tablet} {
    display: grid;
    margin: 48px 0;
    padding: 0;
    grid-gap: 10px;
    grid-template-columns: repeat(24, minmax(0, 1fr));
  }

  @media ${({ theme }) => theme.device.laptop} {
    margin: 80px auto 40px;
  }

  @media ${({ theme }) => theme.device.desktopL} {
    margin: 130px auto 40px;
  }
`;

const InfoHeader = styled(Flex)`
  margin-bottom: 32px;
`;

const List = styled.div`
  display: grid;
  grid-gap: 40px;
  margin-bottom: 40px;
  @media ${({ theme }) => theme.device.tablet} {
    margin-bottom: 0;
  }
`;

const DateWrapper = styled.div`
  display: flex;
  grid-gap: 10px;
  flex-direction: column;
  width: 100%;
`;

const LineGraphContainer = styled.div`
  height: 120px;
  width: 100%;
`;

const StyledHint = styled(Hint)`
  background: #f2c94c;
  opacity: 70%;
  border-radius: 10px;
  margin-bottom: 40px;
  margin: 0 auto;
  @media ${({ theme }) => theme.device.tablet} {
    margin: 0 48px;
  }

  @media ${({ theme }) => theme.device.mobileS} {
    margin: 0 20px;
    padding: 10px;
  }

  @media ${({ theme }) => theme.device.tablet} {
    padding: 30px;
  }
  @media ${({ theme }) => theme.device.laptop} {
    margin: 0 170px;
  }

  @media ${({ theme }) => theme.device.desktopL} {
    margin: 0 130px;
  }
`;
